import React, { useState } from 'react';
import SummarySidebar from './SummarySidebar';
import useConfigurationSelection from 'client/hooks/useConfigurationSelection';
import SuccessConfirmation from 'client/pages/Summary/SuccessConfirmation';
import ActionModal from '@ff/ui/Modal/presets/ActionModal';
import DialogModal from 'client/v3/components/CustomizedDialogModal';
import url from 'client/utils/url';
import PATHS from 'client/utils/paths';
import useConvertPreorder from 'client/hooks/mutations/useConvertPreorder';
import useCreateOrder from 'client/hooks/mutations/useCreateOrder';
import useUser from 'client/hooks/queries/useUser';
import useUpdateProfile from 'client/hooks/mutations/useUpdateProfile';
import handleGraphQlError from 'client/utils/handleGraphQLErrors';
import useSaveSingleConfiguration from 'client/hooks/mutations/useSaveSingleConfiguration';
import useConfigurations from 'client/hooks/queries/useConfigurations';
import useConfig from 'client/hooks/useConfig';
import appHandlers from 'client/utils/appHandlers';
import { useHistory } from 'react-router-dom';
import ga_ids from 'client/ga_ids';

export default function SummaryPage() {

  const [saveConfigurationSuccessConfNum, setSaveConfigurationSuccessConfNum] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [overwriteRequired, setOverwriteRequired] = useState({ required: false, form: undefined });

  const { user, loading: loadingUser } = useUser();

  const history = useHistory();
  const config = useConfig();
  const isApp = config.APPONLY === '1';
  const { configurations, loading: loadingConfigurations } = useConfigurations({
    confirmationNum: config?.CONFIG_NUM,
    options: { skip: !config?.CONFIG_NUM }
  });

  const {
    selectedModel,
    selectedVehicleOptions,
    configurationSummary,
    order,
    loading: loadingConfigurationSelection
  } = useConfigurationSelection();

  const vehicleModelId = selectedModel?.id;
  const vehicleOptionIds = selectedVehicleOptions?.map((o) => o.id) ?? [];
  const newOrder = !order;

  const [saveSingleConfigurationMutation] = useSaveSingleConfiguration();
  const [convertPreorder] = useConvertPreorder();
  const [createOrder] = useCreateOrder();
  const [updateProfile] = useUpdateProfile();

  const updateUserInfo = async (data) => {
    const userInfo = getUserInfo(data);
    const changes = diffUserData(user, userInfo);
    const hasChanges = Object.keys(changes).length > 0;

    if (hasChanges) {
      await updateProfile({ variables: { profileForm: changes } });
    }
  }

  const handleSave = async (data, form, overwrite) => {
    try {
      if (!vehicleModelId) {
        throw new Error('vehicleModelId is required');
      }

      if (data) {
        await updateUserInfo(data);
      }
      const result = await saveSingleConfigurationMutation({
        variables: { configuration: { vehicleModelId, vehicleOptionIds, overwrite } }
      });
      if (result.data?.saveSingleConfiguration.overwriteRequired) {
        setOverwriteRequired({
          required: true,
          form,
        })
      } else {
        setOverwriteRequired({ required: false, form: undefined });
        setSaveConfigurationSuccessConfNum(result.data?.saveSingleConfiguration.confirmationNum || null);
      }

    } catch (error) {
      const errors = handleGraphQlError(error);

      form.setSubmitError('saveConfiguration', errors[0]);
    }
  }

  // update preorder to order only, no payment
  const handleUpdateClick = async (data, form) => {
    try {
      if (!order) {
        throw new Error('order is required');
      }

      await updateUserInfo(data);
      await convertPreorder({
        variables: {
          preorder: {
            orderNum: order.orderNum,
            vehicleConfiguration: {
              modelId: vehicleModelId,
              optionIds: vehicleOptionIds
            },
            isOnWaitlist: data.joinWaitlist,
            // TODO
            // referralCode,
          }
        }
      });

      if (isApp) {
        history.push(url.getPageNavigationV3('update-success', { APPONLY: 1, NO_BACK_BUTTON: 1 }))
      } else {
        setUpdateSuccess(true);
      }

    } catch (error) {
      const errors = handleGraphQlError(error);

      form.setSubmitError('updateOrder', errors[0]);
    }
  }

  const handlePayment = async (data, form) => {
    if (!(configurationSummary && configurationSummary.dueAmount > 0)) {
      console.error('payment is not needed')
      return;
    }

    try {
      const payment = {
        billingFirstName: data.billingFirstName,
        billingLastName: data.billingLastName,
        billingAddress1: data.billingAddress,
        billingAddress2: data.billingAddress2,
        billingCity: data.billingCity,
        billingState: data.billingState,
        billingZipcode: data.billingZipCode,
        // TODO or NOTE: hard coded country, no input from UI
        billingCountry: 'US',
        creditCardNum: data.cardNumber,
        creditCardExpYear: data.expYear,
        creditCardExpMonth: data.expMonth,
        cvv: data.cvv,
      }

      await updateUserInfo(data);

      if (newOrder) {
        await createOrder({
          variables: {
            order: {
              orderType: 'Preorder',
              referralCode: data.referralCode,
              // when configuration id is present, use current configuration for preorder
              vehicleConfigurationId: configurations?.[0] ? configurations[0].id : undefined,
              vehicleConfiguration: {
                modelId: vehicleModelId,
                optionIds: vehicleOptionIds
              },
              payment,
              isOnWaitlist: data.joinWaitlist,
            }
          }
        });
      } else {
        // update current preorder or old preorder without configuration
        await convertPreorder({
          variables: {
            preorder: {
              orderNum: order.orderNum,
              vehicleConfiguration: {
                modelId: vehicleModelId,
                optionIds: vehicleOptionIds
              },
              payment
            }
          }
        });
      }

      if (isApp) {
        history.push(url.getPageNavigationV3('success-payment', { APPONLY: 1, NO_BACK_BUTTON: 1 }))
      } else {
        setPaymentSuccess(true);
      }

    } catch (error) {
      const errors = handleGraphQlError(error);

      form.setSubmitError('payment', errors[0]);
    }

  }

  const handleGoToMyVehicleCollection = () => {
    if (isApp) {
      setTimeout(appHandlers.getHandlerByKey('OpenVehicleCollection'), 10);
    } else {
      window.location.href = url.getSameRegionPath(`${PATHS.MY_FF}`)
    }
  }

  if (loadingConfigurationSelection || loadingUser || loadingConfigurations) return null;

  return (
    <>
      <SummarySidebar
        newOrder={newOrder}
        configurationSummary={configurationSummary}
        onSaveClick={handleSave}
        onUpdateClick={handleUpdateClick}
        onPaymentClick={handlePayment}
      />
      <SuccessConfirmation
        open={!!saveConfigurationSuccessConfNum}
        confirmationNum={saveConfigurationSuccessConfNum}
      />
      <ActionModal
        id={ga_ids.preorder_success}
        iconName='ff-icon-check_circle_outline'
        title='Successful payment'
        actionText='Go to My Vehicle Collection'
        open={paymentSuccess}
        onAction={handleGoToMyVehicleCollection}
        backdrop
      />
      <ActionModal
        id={ga_ids.preorder_update_success}
        iconName='ff-icon-check_circle_outline'
        title='Successful update'
        actionText='Go to My Vehicle Collection'
        open={updateSuccess}
        onAction={handleGoToMyVehicleCollection}
        backdrop
      />
      <DialogModal
        message='You already have a configuration in your design, do you want to replace the previous one?'
        actionText='Replace'
        cancelText='Cancel'
        open={overwriteRequired.required}
        onCancel={() => setOverwriteRequired({ required: false, form: undefined })}
        onClose={() => setOverwriteRequired({ required: false, form: undefined })}
        onAction={() => handleSave(null, overwriteRequired.form, true)}
      />
    </>
  );
}

function getUserInfo(data) {
  const {
    firstName,
    lastName,
    phoneNumber,
    zipCode,
  } = data;

  return {
    firstName,
    lastName,
    mobile: phoneNumber,
    addressCode: zipCode
  };
}

function diffUserData(user, data) {
  const changes = {};

  Object.keys(data).forEach((key) => {
    if (data[key] !== user[key]) {
      changes[key] = data[key];
    }
  });
  return changes;
}
